import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../components/Icon';
import Toggle from '../components/Toggle';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination, { CARD_PAGE_SIZE, usePagedRows } from '../components/Pagination';
import { useToast } from '../components/Toast';
import { extractApiError } from '../api/legalServices';
import { uploadCategoryImage } from '../api/uploads';
import {
  CategorySection,
  ServiceCategory,
  ServiceCategoryInput,
  createServiceCategory,
  deleteServiceCategory,
  listServiceCategories,
  toggleServiceCategory,
  updateServiceCategory,
} from '../api/serviceCategories';

type ModalMode =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; category: ServiceCategory };

interface FormState {
  name: string;
  section: CategorySection;
  description: string;
  image: string | null;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  section: 'legal',
  description: '',
  image: null,
  isActive: true,
};

const SECTION_LABEL: Record<CategorySection, string> = {
  legal: 'Legal Services',
  maintenance: 'Property Maintenance',
};

const fromCategory = (c: ServiceCategory): FormState => ({
  name: c.name ?? '',
  section: c.section ?? 'legal',
  description: c.description ?? '',
  image: c.image ?? null,
  isActive: c.isActive,
});

const toPayload = (f: FormState): ServiceCategoryInput => ({
  name: f.name.trim(),
  section: f.section,
  description: f.description.trim() || null,
  image: f.image,
  isActive: f.isActive,
});

const ServiceCategories: React.FC = () => {
  const { toast } = useToast();
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalMode>({ kind: 'closed' });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [busyToggleId, setBusyToggleId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ServiceCategory | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await listServiceCategories();
      setCategories(list);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load categories'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = useMemo(
    () => categories.filter((c) => c.isActive).length,
    [categories]
  );

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setModal({ kind: 'add' });
  };

  const openEdit = (c: ServiceCategory) => {
    setForm(fromCategory(c));
    setModal({ kind: 'edit', category: c });
  };

  const closeModal = () => {
    if (saving || uploading) return;
    setModal({ kind: 'closed' });
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handlePickImage = () => fileInputRef.current?.click();

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadCategoryImage(file);
      setField('image', url);
      toast('Image uploaded', 'success');
    } catch (err) {
      toast(extractApiError(err, 'Image upload failed'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleToggle = async (c: ServiceCategory) => {
    setBusyToggleId(c.id);
    setCategories((prev) =>
      prev.map((x) => (x.id === c.id ? { ...x, isActive: !x.isActive } : x))
    );
    try {
      const updated = await toggleServiceCategory(c.id);
      setCategories((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
      toast(
        updated.name + ' ' + (updated.isActive ? 'activated' : 'deactivated'),
        updated.isActive ? 'success' : 'info'
      );
    } catch (err) {
      setCategories((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, isActive: c.isActive } : x))
      );
      toast(extractApiError(err, 'Failed to toggle category'), 'error');
    } finally {
      setBusyToggleId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast('Category name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (modal.kind === 'add') {
        const created = await createServiceCategory(toPayload(form));
        setCategories((prev) => [...prev, created]);
        toast(created.name + ' created', 'success');
      } else if (modal.kind === 'edit') {
        const updated = await updateServiceCategory(
          modal.category.id,
          toPayload(form)
        );
        setCategories((prev) =>
          prev.map((x) => (x.id === updated.id ? updated : x))
        );
        toast(updated.name + ' updated', 'success');
      }
      setModal({ kind: 'closed' });
    } catch (err) {
      toast(extractApiError(err, 'Save failed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = () => {
    if (modal.kind !== 'edit') return;
    setPendingDelete(modal.category);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const c = pendingDelete;
    setDeleting(true);
    try {
      await deleteServiceCategory(c.id);
      setCategories((prev) => prev.filter((x) => x.id !== c.id));
      toast(c.name + ' deleted', 'info');
      setPendingDelete(null);
      setModal({ kind: 'closed' });
    } catch (err) {
      toast(extractApiError(err, 'Delete failed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const modalTitle =
    modal.kind === 'add'
      ? 'Add New Category'
      : modal.kind === 'edit'
      ? 'Edit: ' + modal.category.name
      : '';

  // Cards: 12 per page (three or four rows).
  const { page, setPage, pageRows, total } = usePagedRows(categories, CARD_PAGE_SIZE);

  return (
    <div>
      <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
        <div className="text-sm text-g500">
          {loading
            ? 'Loading categories...'
            : activeCount + ' of ' + categories.length + ' categories active'}
        </div>
        <button
          className="btn btn-primary"
          onClick={openAdd}
          disabled={loading || !!loadError}
        >
          <Icon name="plus" size={16} color="#fff" /> Add New Category
        </button>
      </div>

      {loadError && (
        <div
          className="card p-5 mb-5"
          role="alert"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load categories</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {loading && !loadError && (
        <div className="card p-12 text-center text-g500">
          <Icon name="package" size={28} color="var(--g400)" /> Loading...
        </div>
      )}

      {!loading && !loadError && categories.length === 0 && (
        <div className="card p-12 flex flex-col items-center text-center">
          <div
            className="rounded-[20px] flex items-center justify-center mb-5"
            style={{ width: 72, height: 72, background: 'var(--g100)' }}
          >
            <Icon name="package" size={32} color="var(--g400)" />
          </div>
          <div className="text-lg font-semibold text-ink mb-1.5">
            No categories yet
          </div>
          <div className="text-sm text-g500 mb-5" style={{ maxWidth: 420 }}>
            Click "Add New Category" to create your first service category.
          </div>
          <button className="btn btn-primary" onClick={openAdd}>
            <Icon name="plus" size={16} color="#fff" /> Add New Category
          </button>
        </div>
      )}

      {!loading && !loadError && categories.length > 0 && (
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          }}
        >
          {pageRows.map((c, i) => (
            <div
              key={c.id}
              className="card flex flex-col animate-fadeIn overflow-hidden"
              style={{
                opacity: c.isActive ? 1 : 0.55,
                animationDelay: i * 40 + 'ms',
                transition: 'opacity .2s ease',
              }}
            >
              {c.image ? (
                <img
                  src={c.image}
                  alt={c.name}
                  style={{
                    width: '100%',
                    height: 140,
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : (
                <div
                  className="flex items-center justify-center"
                  style={{ width: '100%', height: 140, background: 'var(--g100)' }}
                >
                  <Icon name="package" size={32} color="var(--g400)" />
                </div>
              )}
              <div className="flex flex-col gap-3 p-5" style={{ flex: 1 }}>
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="text-base font-bold mb-1">{c.name}</div>
                    <span
                      className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
                      style={{
                        background: 'var(--brand-light)',
                        color: 'var(--brand)',
                      }}
                    >
                      {SECTION_LABEL[c.section]}
                    </span>
                  </div>
                  <Toggle
                    on={c.isActive}
                    onChange={() => {
                      if (busyToggleId !== c.id) handleToggle(c);
                    }}
                    ariaLabel={'Toggle ' + c.name}
                  />
                </div>
                <div className="text-[13px] text-g500 leading-relaxed" style={{ flex: 1 }}>
                  {c.description || (
                    <span className="italic text-g400">No description</span>
                  )}
                </div>
                <div
                  className="flex justify-end items-center pt-2"
                  style={{ borderTop: '1px solid var(--g100)' }}
                >
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => openEdit(c)}
                  >
                    <Icon name="pencil" size={14} /> Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && !loadError && (
        <Pagination page={page} pageSize={CARD_PAGE_SIZE} total={total} onChange={setPage} />
      )}

      {/* Add/Edit modal */}
      <Modal
        open={modal.kind !== 'closed'}
        title={modalTitle}
        onClose={closeModal}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="form-label">Category Name</label>
            <input
              className="form-input"
              placeholder="e.g. Eviction Services"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              autoFocus
              disabled={saving}
            />
          </div>

          <div className="mb-4">
            <label className="form-label">Description</label>
            <textarea
              rows={3}
              className="form-input"
              placeholder="Describe the category..."
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="mb-4">
            <label className="form-label">Image</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleImageSelected}
            />
            {form.image ? (
              <div
                className="flex items-center gap-3 p-2.5 rounded-lg"
                style={{ border: '1px solid var(--g200)' }}
              >
                <img
                  src={form.image}
                  alt="Category"
                  style={{
                    width: 72,
                    height: 48,
                    objectFit: 'cover',
                    borderRadius: 8,
                  }}
                />
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handlePickImage}
                  disabled={saving || uploading}
                >
                  {uploading ? 'Uploading...' : 'Replace'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ color: 'var(--red)' }}
                  onClick={() => setField('image', null)}
                  disabled={saving || uploading}
                  aria-label="Remove image"
                >
                  <Icon name="x-circle" size={14} color="var(--red)" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePickImage}
                disabled={saving || uploading}
              >
                <Icon name="plus" size={14} />{' '}
                {uploading ? 'Uploading...' : 'Upload Image'}
              </button>
            )}
            <div className="text-[12px] text-g400 mt-1.5">
              JPG, PNG, WEBP or GIF — max 5MB.
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label">Status</label>
            <div className="flex items-center gap-2.5 py-1.5">
              <Toggle
                on={form.isActive}
                onChange={() => setField('isActive', !form.isActive)}
              />
              <span className="text-[13px] text-g500">
                {form.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div
            className="flex gap-2.5 justify-end mt-6"
            style={{ flexWrap: 'wrap' }}
          >
            {modal.kind === 'edit' && (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: 'var(--red)', marginRight: 'auto' }}
                onClick={requestDelete}
                disabled={saving}
              >
                <Icon name="x-circle" size={14} color="var(--red)" /> Delete
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeModal}
              disabled={saving || uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || uploading}
            >
              {saving
                ? 'Saving...'
                : modal.kind === 'add'
                ? 'Create Category'
                : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Themed delete confirmation */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete Category"
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Keep"
        busy={deleting}
        message={
          <>
            Are you sure you want to delete{' '}
            <strong style={{ color: 'var(--ink)' }}>
              {pendingDelete?.name}
            </strong>
            ?
            <br />
            This cannot be undone.
          </>
        }
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
};

export default ServiceCategories;
